import React from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import css from '@styled-system/css';

import { PrismicRichText } from '@prismicio/react';
import { Text, Box, Accordion } from '../../../design-system';
import { CmsText } from '../../cms/types';
import { TITLE_LEGAL } from '../locale';
import { hasValidCmsText } from '../../home/utils';

type Props = {
  links: {
    primary: {
      title: CmsText;
    };
    items: {
      color: string;
      text: CmsText;
    }[];
  }[];
};
type LinkTextProps = {
  cmsColor?: string;
};
const LinkText = styled(Text)<LinkTextProps>(
  css({
    p: {
      my: ['m', 's'],
    },
    'p > a': {
      fontSize: 's',
      color: 'MAIN_GREY',
      textDecoration: 'none',
      '@media(hover: hover)': {
        ':hover': {
          opacity: 0.8,
        },
      },
    },
  }),
  ({ cmsColor }) =>
    cmsColor && {
      'p > a': {
        color: cmsColor,
      },
    }
);
const Links = ({ links }: Props) => {
  return (
    <>
      <Box
        display={['none', 'none', 'grid']}
        justifyContent="center"
        gridGap="xxxl"
        gridAutoFlow="column"
      >
        {links.map((item, index) => (
          <div key={index}>
            <Text mb="s" fontWeight="bold" textTransform="uppercase">
              {item.primary?.title?.[0]?.text ?? ''}
            </Text>
            {(item.items ?? []).map(
              (field, index) =>
                hasValidCmsText(field.text) && (
                  <LinkText key={index} cmsColor={field.color}>
                    <PrismicRichText field={field.text} />
                  </LinkText>
                )
            )}
          </div>
        ))}
      </Box>
      <Box display={['block', 'block', 'none']}>
        <Accordion
          id="mobile-footer-links"
          items={[
            ...links.map((item, index) => {
              const title = item.primary?.title?.[0]?.text ?? '';
              if (!title) {
                return { title: '', content: '', index: 0 };
              }
              return {
                title: (
                  <Text fontWeight="bold" textTransform="uppercase">
                    {title}
                  </Text>
                ),
                content: (item.items ?? []).map(
                  (field, index) =>
                    hasValidCmsText(field.text) && (
                      <Box key={index} mx="s">
                        <LinkText cmsColor={field.color}>
                          <PrismicRichText field={field.text} />
                        </LinkText>
                      </Box>
                    )
                ),
                index,
              };
            }),
            {
              title: (
                <Text fontWeight="bold" textTransform="uppercase">
                  {TITLE_LEGAL}
                </Text>
              ),
              content: (
                <>
                  <Box mx="s">
                    <LinkText>
                      <p>
                        <Link
                          className="anchor-animated"
                          data-testid="mobile-link-to-cgv"
                          to="/cgv"
                          aria-label="Lien pour accéder aux conditions générales de vente"
                        >
                          CGV
                        </Link>
                      </p>
                    </LinkText>
                  </Box>
                  <Box mx="s">
                    <LinkText>
                      <p>
                        <Link
                          className="anchor-animated"
                          data-testid="mobile-link-to-legal-notices"
                          to="/mentions-legales"
                          aria-label="Lien pour accéder aux mentions légales"
                        >
                          Mentions légales
                        </Link>
                      </p>
                    </LinkText>
                  </Box>
                  <Box mx="s">
                    <LinkText>
                      <p>
                        <Link
                          className="anchor-animated"
                          data-testid="mobile-link-to-privacy-policy"
                          to="/politique-de-confidentialite"
                          aria-label="Lien pour accéder à la politique de confidentialité"
                        >
                          Politique de confidentialité
                        </Link>
                      </p>
                    </LinkText>
                  </Box>
                  <Box mx="s">
                    <LinkText>
                      <p>
                        <Link
                          className="anchor-animated"
                          data-testid="mobile-link-to-cookies-policy"
                          to="/politique-de-cookies"
                          aria-label="Lien pour accéder à la politique de cookies"
                        >
                          Politique de cookies
                        </Link>
                      </p>
                    </LinkText>
                  </Box>
                </>
              ),
              index: !links?.length ? 0 : links.length + 1,
            },
          ]}
        />
      </Box>
    </>
  );
};
export { Links };
